import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { hasHistoryContent } from '@/frontend/pages/Dashboard/AgentChats/historyVisibility'
import {
  abortSessionOverHttp,
  agentChatApiScope,
  archiveSessionOverHttp,
  branchSessionOverHttp,
  createSessionOverHttp,
  deleteSessionOverHttp,
  loadSessionOverHttp,
  pinSessionOverHttp,
  renameSessionOverHttp,
  retrySessionOverHttp,
  sendMessageOverHttp,
  steerSessionOverHttp,
  submitMessageFeedbackOverHttp,
} from '@/frontend/pages/Dashboard/AgentChats/api'
import { randomUuid } from '@/frontend/lib/randomUuid'
import { validateAgentChatFiles } from '@/frontend/pages/Dashboard/AgentChats/attachments'
import type {
  UserQuestionAnswer,
  AgentChatDigitalWorker,
  AgentChatMessage,
  AgentChatProject,
  AgentChatSessionDetail,
  AgentChatSessionSummary,
} from '@/frontend/pages/Dashboard/AgentChats/types'
import { useAgentChatsChannel } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatsChannel'
import { useAgentChatEvents } from '@/frontend/features/AgentChats/events/useAgentChatEvents'
import type { AgentEvent } from '@/frontend/features/AgentChats/events/agentEvents'
import { mergeMessages, mergeNativeMessage, upsertMessage } from '@/frontend/features/AgentChats/events/messageSnapshots'

interface PendingSubmission {
  kind: 'message' | 'steering'
  submissionId: string
  draft: string
  attachments: File[]
  attachmentSignedBlobIds?: string[]
  onError?: (pending: PendingSubmission) => void
  onRecoveredCommit?: () => void
  pageContext?: Record<string, unknown>
  questionAnswer?: UserQuestionAnswer
}

interface BufferedMessageEvent {
  message: AgentChatMessage
  committedSubmission: boolean
}

type SubmissionPhase = 'idle' | 'uploading' | 'sending'

interface UseAgentChatControllerOptions {
  organizationId: string | null
  digitalWorker: AgentChatDigitalWorker | null
  realtimeEnabled?: boolean
  initialSession?: AgentChatSessionDetail | null
  initialSessions?: AgentChatSessionSummary[]
  initialProjects?: AgentChatProject[]
  // Origem persistida quando ESTE controller cria a sessão no primeiro envio.
  // Só valores allowlisted no servidor viram metadata (ex.: criação de
  // Processos); os demais seguem ignorados.
  sessionCreateSource?: string
  sessionCreateFdeProjectId?: string
  // Read at send time, for persistent controllers whose page can change
  // under them (the floating widget). The server still validates the scope.
  getSessionCreateScope?: () => { source: string; fdeProjectId: string } | null
  syncInitialSessionFromProps?: boolean
  onSessionChange?: (sessionId: string | null) => void
  onReconnect?: () => void
  getPageContext?: () => Record<string, unknown>
}

const NO_SESSION_SUMMARIES: AgentChatSessionSummary[] = []
const NO_PROJECTS: AgentChatProject[] = []
const SUBMISSION_RESPONSE_TIMEOUT_MS = 10_000
const SESSION_SYNC_STALE_AFTER_MS = 10_000

export function useAgentChatController({
  organizationId,
  digitalWorker,
  realtimeEnabled = true,
  initialSession = null,
  initialSessions = NO_SESSION_SUMMARIES,
  initialProjects = NO_PROJECTS,
  sessionCreateSource,
  sessionCreateFdeProjectId,
  getSessionCreateScope,
  syncInitialSessionFromProps = true,
  onSessionChange,
  onReconnect,
  getPageContext,
}: UseAgentChatControllerOptions) {
  const [session, setSession] = useState<AgentChatSessionDetail | null>(initialSession)
  const [sessions, setSessions] = useState<AgentChatSessionSummary[]>(() =>
    sortSessionSummaries(initialSessions),
  )
  const [projects, setProjects] = useState<AgentChatProject[]>(initialProjects)
  const [isNewConversation, setIsNewConversation] = useState(initialSession === null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [submissionPhase, setSubmissionPhase] = useState<SubmissionPhase>('idle')
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  const activeSessionIdRef = useRef<string | null>(initialSession?.id ?? null)
  const previousInitialSessionIdRef = useRef<string | null>(initialSession?.id ?? null)
  const previousWorkerIdRef = useRef<string | null>(digitalWorker?.id ?? null)
  // Any explicit reset invalidates late HTTP completions from the prior
  // worker/session so they cannot reactivate discarded conversation state.
  const conversationGenerationRef = useRef(0)
  const httpSubmissionRef = useRef<{
    generation: number
    controller: AbortController
    submissionId: string
    committed: boolean
  } | null>(null)
  const abortRequestsRef = useRef<Map<string, Promise<boolean>>>(new Map())
  const pendingSubmissionRef = useRef<PendingSubmission | null>(null)
  const retrySubmissionRef = useRef<PendingSubmission | null>(null)
  const handleAuthoritativeSessionRef = useRef<(session: AgentChatSessionDetail) => void>(() => undefined)
  const shouldResendAfterReconnectRef = useRef(false)
  const answeredUserMessageIdsRef = useRef<Set<string>>(new Set())
  const syncRequestRef = useRef<{
    sessionId: string
    controller: AbortController
    startedAt: number
  } | null>(null)
  const hydratingSessionRef = useRef<{
    sessionId: string
    messages: BufferedMessageEvent[]
  } | null>(null)

  useEffect(() => () => {
    httpSubmissionRef.current?.controller.abort()
    httpSubmissionRef.current = null
    syncRequestRef.current?.controller.abort()
    syncRequestRef.current = null
    hydratingSessionRef.current = null
    abortRequestsRef.current.clear()
  }, [])

  const resetConversation = useCallback((nextSession: AgentChatSessionDetail | null) => {
    conversationGenerationRef.current += 1
    httpSubmissionRef.current?.controller.abort()
    httpSubmissionRef.current = null
    activeSessionIdRef.current = nextSession?.id ?? null
    previousInitialSessionIdRef.current = nextSession?.id ?? null
    setSession(nextSession)
    setIsNewConversation(nextSession === null)
    setAttachmentError(null)
    setSubmissionPhase('idle')
    setSubmissionError(null)
    setRenameError(null)
    pendingSubmissionRef.current = null
    retrySubmissionRef.current = null
    shouldResendAfterReconnectRef.current = false
    answeredUserMessageIdsRef.current = new Set()
    syncRequestRef.current?.controller.abort()
    syncRequestRef.current = null
    hydratingSessionRef.current = null
  }, [])

  useEffect(() => {
    activeSessionIdRef.current = isNewConversation ? null : session?.id ?? null
  }, [isNewConversation, session?.id])

  useEffect(() => {
    setSessions(sortSessionSummaries(initialSessions))
    setProjects(initialProjects)

    const nextWorkerId = digitalWorker?.id ?? null
    const workerChanged = previousWorkerIdRef.current !== nextWorkerId
    const nextInitialSessionId = initialSession?.id ?? null
    const initialSessionChanged = previousInitialSessionIdRef.current !== nextInitialSessionId

    if (workerChanged) {
      previousWorkerIdRef.current = nextWorkerId
      resetConversation(initialSession)
      return
    }

    if (syncInitialSessionFromProps && initialSessionChanged) {
      resetConversation(initialSession)
      return
    }

    if (!syncInitialSessionFromProps && activeSessionIdRef.current && !initialSessions.some((item) => (
      item.id === activeSessionIdRef.current
    ))) {
      resetConversation(initialSession)
      return
    }

    if (syncInitialSessionFromProps && initialSession) {
      setSession((current) => (current ? mergeSession(current, initialSession) : initialSession))
    }
  }, [
    digitalWorker?.id,
    initialSession,
    initialSessions,
    initialProjects,
    resetConversation,
    syncInitialSessionFromProps,
  ])

  const consumeHydratingMessages = useCallback((nextSession: AgentChatSessionDetail) => {
    const hydration = hydratingSessionRef.current
    if (!hydration || hydration.sessionId !== nextSession.id) return nextSession

    hydratingSessionRef.current = null
    return hydration.messages.reduce(
      (current, event) => applyRealtimeMessage(current, event.message, event.committedSubmission),
      nextSession,
    )
  }, [])

  const activateSessionDetail = useCallback((nextSession: AgentChatSessionDetail, notify = false) => {
    const hydratedSession = consumeHydratingMessages(nextSession)
    activeSessionIdRef.current = hydratedSession.id
    setSession(hydratedSession)
    setSessions((current) => upsertSessionSummary(current, detailToSummary(hydratedSession)))
    setIsNewConversation(false)
    setSubmissionError(null)
    setAttachmentError(null)
    setRenameError(null)
    if (notify) onSessionChange?.(hydratedSession.id)
    return hydratedSession
  }, [consumeHydratingMessages, onSessionChange])

  const acceptSessionDetail = useCallback((
    nextSession: AgentChatSessionDetail,
    clearTransientErrors = true,
  ) => {
    if (nextSession.id !== activeSessionIdRef.current) return null

    const hydratedSession = consumeHydratingMessages(nextSession)
    setSession((current) => (current ? mergeSession(current, hydratedSession) : hydratedSession))
    setSessions((current) => upsertSessionSummary(current, detailToSummary(hydratedSession)))
    setIsNewConversation(false)
    if (clearTransientErrors) {
      setSubmissionError(null)
      setRenameError(null)
    }
    return hydratedSession
  }, [consumeHydratingMessages])

  const settleCommittedHttpSubmission = useCallback(() => {
    const operation = httpSubmissionRef.current
    const pending = pendingSubmissionRef.current
    if (!operation?.committed || !pending || operation.submissionId !== pending.submissionId) {
      return false
    }

    httpSubmissionRef.current = null
    pendingSubmissionRef.current = null
    if (retrySubmissionRef.current?.submissionId === pending.submissionId) {
      retrySubmissionRef.current = null
    }
    operation.controller.abort()
    setSubmissionPhase('idle')
    pending.onRecoveredCommit?.()
    return true
  }, [])

  const reloadForProtocolMismatch = useCallback(() => {
    if (!pendingSubmissionRef.current) return

    shouldResendAfterReconnectRef.current = false
    httpSubmissionRef.current?.controller.abort()
    window.location.reload()
  }, [])

  const recordSubmissionCommit = useCallback((message: AgentChatMessage) => {
    const pending = pendingSubmissionRef.current
    const retry = retrySubmissionRef.current
    const submission = pending ?? (!httpSubmissionRef.current ? retry : null)
    if (!submission || !pendingSubmissionMatchesCommittedMessage(submission, message)) {
      return false
    }

    const httpSubmission = httpSubmissionRef.current
    if (httpSubmission) {
      if (httpSubmission.submissionId !== submission.submissionId) return false

      httpSubmission.committed = true
      return true
    }

    if (!pending) {
      retrySubmissionRef.current = null
      setSubmissionError(null)
      retry?.onRecoveredCommit?.()
      setSubmissionPhase('idle')
      return true
    }

    pendingSubmissionRef.current = null
    if (retrySubmissionRef.current?.submissionId === pending.submissionId) {
      retrySubmissionRef.current = null
    }
    setSubmissionPhase('idle')
    pending.onRecoveredCommit?.()
    return true
  }, [])

  const completeCableSubmissionById = useCallback((clientSubmissionId: string | null) => {
    const pending = pendingSubmissionRef.current
    if (
      httpSubmissionRef.current ||
      !pending ||
      !clientSubmissionId ||
      pending.submissionId !== clientSubmissionId
    ) {
      return false
    }

    pendingSubmissionRef.current = null
    if (retrySubmissionRef.current?.submissionId === pending.submissionId) {
      retrySubmissionRef.current = null
    }
    setSubmissionPhase('idle')
    pending.onRecoveredCommit?.()
    return true
  }, [])

  const syncSessionOverHttp = useCallback(() => {
    const sessionId = activeSessionIdRef.current
    if (!sessionId) return

    const activeRequest = syncRequestRef.current
    if (
      activeRequest?.sessionId === sessionId &&
      Date.now() - activeRequest.startedAt < SESSION_SYNC_STALE_AFTER_MS
    ) return

    activeRequest?.controller.abort()
    const controller = new AbortController()
    syncRequestRef.current = { sessionId, controller, startedAt: Date.now() }

    loadSessionOverHttp(sessionId, { signal: controller.signal })
      .then((loadedSession) => {
        if (controller.signal.aborted) return

        const completedHydration = hydratingSessionRef.current?.sessionId === sessionId
        const acceptedSession = acceptSessionDetail(loadedSession)
        if (acceptedSession) {
          handleAuthoritativeSessionRef.current(acceptedSession)
          if (completedHydration && !httpSubmissionRef.current) setSubmissionPhase('idle')
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return

        console.error('[agent-chat] sync polling failed:', error)
      })
      .finally(() => {
        if (syncRequestRef.current?.controller === controller) {
          syncRequestRef.current = null
        }
      })
  }, [acceptSessionDetail])

  const acceptCommittedNewSessionMessage = useCallback((sessionId: string, message: AgentChatMessage) => {
    const pending = pendingSubmissionRef.current
    const retry = retrySubmissionRef.current
    const submission = httpSubmissionRef.current ? pending : retry
    if (
      !submission ||
      activeSessionIdRef.current ||
      (httpSubmissionRef.current && httpSubmissionRef.current.submissionId !== submission.submissionId) ||
      !pendingSubmissionMatchesMessage(submission, message)
    ) {
      return false
    }

    const recoveredAfterTimeout = !pending
    recordSubmissionCommit(message)
    if (recoveredAfterTimeout) setSubmissionPhase('sending')

    activeSessionIdRef.current = sessionId
    const hydration = { sessionId, messages: [] }
    hydratingSessionRef.current = hydration
    setSubmissionError(null)
    setAttachmentError(null)
    onSessionChange?.(sessionId)

    syncRequestRef.current?.controller.abort()
    const controller = new AbortController()
    syncRequestRef.current = { sessionId, controller, startedAt: Date.now() }

    loadSessionOverHttp(sessionId, { signal: controller.signal })
      .then((loadedSession) => {
        if (
          controller.signal.aborted ||
          activeSessionIdRef.current !== sessionId ||
          hydratingSessionRef.current !== hydration
        ) return

        const activatedSession = activateSessionDetail(loadedSession)
        handleAuthoritativeSessionRef.current(activatedSession)
        if (!httpSubmissionRef.current) setSubmissionPhase('idle')
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return

        console.error('[agent-chat] new session sync failed:', error)
      })
      .finally(() => {
        if (syncRequestRef.current?.controller === controller) {
          syncRequestRef.current = null
        }
      })

    return true
  }, [activateSessionDetail, onSessionChange, recordSubmissionCommit])

  // Run activity is folded in its own hook: it is per-turn and disposable,
  // while everything else in this controller is per-conversation and
  // authoritative. Mixing the two lifecycles here is what would make this file
  // grow without bound.
  const { activity, applyEvent, replaceFromReplay, clearActivity, restartActivity } = useAgentChatEvents({
    sessionId: session?.id ?? null,
    pendingTurnId: session?.pending_turn?.id ?? null,
    pendingTurnAttempt: session?.pending_turn?.attempt ?? 0,
    acceptEventsWithoutPendingTurn:
      submissionPhase !== 'idle' || (session?.awaiting_response ?? false),
  })

  const activeSessionId = session?.id
  const pendingTurn = session?.pending_turn
  useEffect(() => {
    if (!activeSessionId || !pendingTurn?.events?.length) return

    replaceFromReplay(activeSessionId, pendingTurn.events)
  }, [activeSessionId, pendingTurn, replaceFromReplay])

  const handleAgentEvent = useCallback((sessionId: string, event: AgentEvent) => {
    applyEvent(sessionId, event)
    if (sessionId !== activeSessionIdRef.current) return
    if (!agentEventMayCompleteTurn(event)) return

    // The terminal stream frame can arrive just before the durable assistant
    // message. Reconcile immediately so a missed/out-of-order summary cannot
    // leave the composer and live-run affordance stuck in a running state.
    syncSessionOverHttp()
  }, [applyEvent, syncSessionOverHttp])

  const removeSessionFromView = useCallback((sessionId: string) => {
    setSessions((current) => current.filter((item) => item.id !== sessionId))
    if (activeSessionIdRef.current !== sessionId) return

    resetConversation(null)
    onSessionChange?.(null)
  }, [onSessionChange, resetConversation])

  const { connectionStatus, loadSession, sendMessage } = useAgentChatsChannel({
    enabled: realtimeEnabled,
    organizationId,
    onAgentEvent: handleAgentEvent,
    onSessionDeleted: removeSessionFromView,
    onReconnect: () => {
      setAttachmentError(null)
      setSubmissionError(null)
      setRenameError(null)
      onReconnect?.()
      shouldResendAfterReconnectRef.current = true
      syncSessionOverHttp()
    },
    onSessionUpsert: (incomingSession) => {
      if (incomingSession.status === 'archived' || incomingSession.hidden_from_history) {
        setSessions((current) => current.filter((candidate) => candidate.id !== incomingSession.id))
      }
      const includeInList =
        incomingSession.status === 'active' &&
        !incomingSession.hidden_from_history &&
        incomingSession.digital_worker_id === digitalWorker?.id &&
        hasHistoryContent(incomingSession, activeSessionIdRef.current)
      if (includeInList) {
        setSessions((current) => upsertSessionSummary(current, incomingSession))
      }

      if (incomingSession.id !== activeSessionIdRef.current) return
      setSession((current) => {
        if (!current) return current

        const processOutcomes = mergeBusinessProcessOutcomes(current, incomingSession)
        // A message event can beat its matching summary without advancing the
        // detail snapshot's clock, so an incomplete summary must not unlock it.
        // Process outcome stamps are monotonic, however, and must survive this
        // guard so the Process canvas can reload after a procedure revision.
        if (
          isOlderSessionSummary(incomingSession, current) ||
          incomingSession.message_count < current.messages.length
        ) {
          return { ...current, ...processOutcomes }
        }
        return {
          ...current,
          ...incomingSession,
          ...processOutcomes,
          can_send: current.can_send,
          messages: current.messages,
          has_visible_user_message:
            current.has_visible_user_message || incomingSession.has_visible_user_message,
        }
      })
    },
    onSessionLoaded: (loadedSession) => {
      if (loadedSession.id !== activeSessionIdRef.current) return
      const acceptedSession = acceptSessionDetail(loadedSession, false)
      if (acceptedSession) handleAuthoritativeSessionRef.current(acceptedSession)
    },
    onMessageCreated: (sessionId, message) => {
      if (sessionId !== activeSessionIdRef.current) {
        if (acceptCommittedNewSessionMessage(sessionId, message)) return

        return
      }

      const committedSubmission = recordSubmissionCommit(message)
      if (committedSubmission) settleCommittedHttpSubmission()
      const replyToMessageId = assistantReplyToMessageId(message)
      if (replyToMessageId) answeredUserMessageIdsRef.current.add(replyToMessageId)

      const hydration = hydratingSessionRef.current
      if (hydration?.sessionId === sessionId) {
        hydration.messages = upsertBufferedMessageEvent(
          hydration.messages,
          { message, committedSubmission },
        )
        return
      }

      setSession((current) => current
        ? applyRealtimeMessage(current, message, committedSubmission)
        : current)

    },
    onMessageSubmitted: (sessionId, messageId, clientSubmissionId, supportsClientSubmissionId) => {
      if (sessionId !== activeSessionIdRef.current) return
      if (httpSubmissionRef.current) return
      if (!supportsClientSubmissionId) {
        reloadForProtocolMismatch()
        return
      }
      if (!completeCableSubmissionById(clientSubmissionId)) return

      setSubmissionError(null)

      const alreadyAnswered = answeredUserMessageIdsRef.current.has(messageId)
      setSession((current) => current ? ({
        ...current,
        awaiting_response: !alreadyAnswered && !hasAssistantResponseFor(current.messages, messageId),
        pending_turn: alreadyAnswered ? null : current.pending_turn,
        response_error: null,
        failed_response_message_id: null,
      }) : current)
      syncSessionOverHttp()
    },
    onSubmissionFailed: (sessionId, error, clientSubmissionId, supportsClientSubmissionId) => {
      if (sessionId && sessionId !== activeSessionIdRef.current) return
      if (httpSubmissionRef.current) return
      if (!supportsClientSubmissionId) {
        reloadForProtocolMismatch()
        return
      }

      const pending = pendingSubmissionRef.current
      if (!pending || !clientSubmissionId || pending.submissionId !== clientSubmissionId) return

      pendingSubmissionRef.current = null
      retrySubmissionRef.current = pending
      setSubmissionPhase('idle')
      setSubmissionError(error)
      pending.onError?.(pending)
    },
  })

  handleAuthoritativeSessionRef.current = (loadedSession) => {
    const pending = pendingSubmissionRef.current ?? (
      httpSubmissionRef.current ? null : retrySubmissionRef.current
    )
    if (!pending) {
      shouldResendAfterReconnectRef.current = false
      return
    }
    if (loadedSession.submission_idempotency !== true) {
      reloadForProtocolMismatch()
      return
    }

    const committed = loadedSession.messages.some(recordSubmissionCommit)
    if (committed) {
      settleCommittedHttpSubmission()
      shouldResendAfterReconnectRef.current = false
      return
    }

    // Steering is delivered through its dedicated HTTP endpoint and can be
    // retried idempotently only while the original run is active. Never feed a
    // failed/ambiguous steering candidate into the ordinary websocket resend.
    if (pending.kind === 'steering') {
      shouldResendAfterReconnectRef.current = false
      return
    }

    const sessionId = activeSessionIdRef.current
    if (
      !shouldResendAfterReconnectRef.current ||
      httpSubmissionRef.current ||
      !sessionId ||
      pending.attachments.length > 0
    ) {
      return
    }

    shouldResendAfterReconnectRef.current = false
    sendMessage(sessionId, pending.draft, pending.submissionId, pending.pageContext, pending.questionAnswer)
  }

  useEffect(() => {
    const hydratingSessionId = hydratingSessionRef.current?.sessionId
    const sessionId = session?.id ?? hydratingSessionId
    if (!sessionId || (!hydratingSessionId && !session?.awaiting_response && submissionPhase === 'idle')) {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      if (activeSessionIdRef.current !== sessionId) {
        window.clearInterval(intervalId)
        return
      }
      if (hydratingSessionRef.current?.sessionId === sessionId) {
        syncSessionOverHttp()
        return
      }
      if (connectionStatus === 'connected' && loadSession(sessionId)) return

      syncSessionOverHttp()
    }, 1000)
    return () => {
      window.clearInterval(intervalId)
      syncRequestRef.current?.controller.abort()
      syncRequestRef.current = null
    }
  }, [
    connectionStatus,
    loadSession,
    session?.awaiting_response,
    session?.id,
    submissionPhase,
    syncSessionOverHttp,
  ])

  const handleNewConversation = useCallback(() => {
    // A server-owned run may continue in its own conversation, but a client
    // submission that has not been durably acknowledged still owns the draft
    // and attachments. Detaching during that small window would abort the only
    // retry path and silently discard the user's input.
    if (
      submissionPhase !== 'idle' ||
      httpSubmissionRef.current ||
      pendingSubmissionRef.current ||
      hydratingSessionRef.current
    ) return

    // A run belongs to its session, not to the chat surface. Detach the active
    // view while the prior server run is active; realtime upserts
    // keep that session in history without making a late response reclaim the
    // new blank conversation.
    resetConversation(null)
    onSessionChange?.(null)
  }, [onSessionChange, resetConversation, submissionPhase])

  const performSend = useCallback(async (
    content: string,
    files: File[],
    submissionId: string,
    attachmentSignedBlobIds?: string[],
    onError?: (pending: PendingSubmission) => void,
    onRecoveredCommit?: () => void,
    pageContext?: Record<string, unknown>,
    questionAnswer?: UserQuestionAnswer,
  ): Promise<boolean> => {
    if (!digitalWorker) {
      setSubmissionError('Escolha um agente para começar.')
      return false
    }

    const conversationGeneration = conversationGenerationRef.current
    const beginHttpSubmission = () => {
      httpSubmissionRef.current?.controller.abort()
      const operation = {
        generation: conversationGeneration,
        controller: new AbortController(),
        submissionId,
        committed: false,
      }
      httpSubmissionRef.current = operation
      const isCurrent = () => (
        httpSubmissionRef.current === operation &&
        operation.generation === conversationGenerationRef.current &&
        !operation.controller.signal.aborted
      )
      return {
        operation,
        isCurrent,
        options: {
          attachmentSignedBlobIds,
          // O upload de anexo precisa saber o agente para o servidor resolver
          // a organização dona dele (chat cross-org do Command Center).
          digitalWorkerId: digitalWorker?.id ?? null,
          clientSubmissionId: submissionId,
          responseTimeoutMs: SUBMISSION_RESPONSE_TIMEOUT_MS,
          signal: operation.controller.signal,
          pageContext,
          questionAnswer,
          onUploadsComplete: () => {
            if (isCurrent()) setSubmissionPhase('sending')
          },
        },
      }
    }
    setSubmissionError(null)
    setRenameError(null)
    setAttachmentError(null)
    pendingSubmissionRef.current = {
      kind: 'message',
      submissionId,
      draft: content,
      attachments: files,
      attachmentSignedBlobIds,
      onError,
      onRecoveredCommit,
      pageContext,
      questionAnswer,
    }
    setSubmissionPhase(files.length > 0 ? 'uploading' : 'sending')

    if (!session) {
      const submission = beginHttpSubmission()
      const scope = sessionCreateSource ? null : getSessionCreateScope?.() ?? null
      try {
        const created = await withSingleTransportRetry(
          () => createSessionOverHttp({
            workerId: digitalWorker.id,
            agentId: digitalWorker.agent_id,
            content,
            source: scope?.source ?? sessionCreateSource,
            fdeProjectId: scope?.fdeProjectId ?? sessionCreateFdeProjectId,
            attachments: files,
            options: submission.options,
          }),
          submission.isCurrent,
          () => submission.operation.committed,
        )
        if (!submission.isCurrent()) return submission.operation.committed
        if (!sessionConfirmsSubmissionProtocol(created, submissionId)) {
          reloadForProtocolMismatch()
          return true
        }

        submission.operation.committed = true
        const committedPending = pendingSubmissionRef.current
        submission.operation.controller.abort()
        httpSubmissionRef.current = null
        pendingSubmissionRef.current = null
        retrySubmissionRef.current = null
        setSubmissionPhase('idle')
        committedPending?.onRecoveredCommit?.()
        const shouldActivateCreatedSession =
          activeSessionIdRef.current !== created.id ||
          hydratingSessionRef.current?.sessionId === created.id
        if (shouldActivateCreatedSession) {
          syncRequestRef.current?.controller.abort()
          syncRequestRef.current = null
          activateSessionDetail(created, true)
        }
        return true
      } catch (error) {
        if (!submission.isCurrent()) return submission.operation.committed

        if (submission.operation.committed) {
          submission.operation.controller.abort()
          httpSubmissionRef.current = null
          pendingSubmissionRef.current = null
          retrySubmissionRef.current = null
          setSubmissionPhase('idle')
          // The commit event may already have started hydration. Replace that
          // request so its later failure cannot consume the only recovery load.
          syncRequestRef.current?.controller.abort()
          syncRequestRef.current = null
          syncSessionOverHttp()
          return true
        }

        submission.operation.controller.abort()
        httpSubmissionRef.current = null
        const pending = pendingSubmissionRef.current
        pendingSubmissionRef.current = null
        setSubmissionPhase('idle')
        if (!pending) return false

        pending.attachmentSignedBlobIds = submission.options.attachmentSignedBlobIds
        retrySubmissionRef.current = pending
        setSubmissionError(error instanceof Error ? error.message : 'Não foi possível enviar a mensagem agora.')
        pending.onError?.(pending)
        return false
      }
    } else if (!questionAnswer && files.length === 0 && sendMessage(session.id, content, submissionId, pageContext, questionAnswer)) {
      return true
    }

    // Attachments and disconnected sockets use the same idempotent HTTP
    // endpoint. Text-only messages prefer the live channel above so reconnect
    // recovery can resend the identical submission id without duplication.
    const submission = beginHttpSubmission()
    try {
      const updated = await withSingleTransportRetry(
        () => sendMessageOverHttp(session.id, content, files, submission.options),
        submission.isCurrent,
        () => submission.operation.committed,
      )
      if (!submission.isCurrent()) return submission.operation.committed
      if (!sessionConfirmsSubmissionProtocol(updated, submissionId)) {
        reloadForProtocolMismatch()
        return true
      }

      submission.operation.committed = true
      const committedPending = pendingSubmissionRef.current
      submission.operation.controller.abort()
      httpSubmissionRef.current = null
      pendingSubmissionRef.current = null
      retrySubmissionRef.current = null
      setSubmissionPhase('idle')
      committedPending?.onRecoveredCommit?.()
      acceptSessionDetail(updated)
      return true
    } catch (error) {
      if (!submission.isCurrent()) return submission.operation.committed

      if (submission.operation.committed) {
        submission.operation.controller.abort()
        httpSubmissionRef.current = null
        pendingSubmissionRef.current = null
        retrySubmissionRef.current = null
        setSubmissionPhase('idle')
        syncSessionOverHttp()
        return true
      }

      submission.operation.controller.abort()
      httpSubmissionRef.current = null
      const pending = pendingSubmissionRef.current
      pendingSubmissionRef.current = null
      setSubmissionPhase('idle')
      if (!pending) return false

      pending.attachmentSignedBlobIds = submission.options.attachmentSignedBlobIds
      retrySubmissionRef.current = pending
      setSubmissionError(error instanceof Error ? error.message : 'Não foi possível enviar a mensagem agora.')
      pending.onError?.(pending)
      return false
    }
  }, [session, digitalWorker, sessionCreateSource, sessionCreateFdeProjectId, getSessionCreateScope, sendMessage, activateSessionDetail, acceptSessionDetail, syncSessionOverHttp, reloadForProtocolMismatch])

  const abortSessionTurn = useCallback((sessionId: string) => {
    const activeRequest = abortRequestsRef.current.get(sessionId)
    if (activeRequest) return activeRequest

    const conversationGeneration = conversationGenerationRef.current
    const isCurrent = () => (
      conversationGenerationRef.current === conversationGeneration &&
      activeSessionIdRef.current === sessionId
    )
    setSubmissionError(null)
    if (isCurrent()) setSubmissionPhase('sending')
    const request = (async () => {
      try {
        const updated = await abortSessionOverHttp(sessionId)
        if (!isCurrent()) return false

        acceptSessionDetail(updated)
        clearActivity()
        return true
      } catch (error) {
        if (!isCurrent()) return false

        setSubmissionError(
          error instanceof Error ? error.message : 'Não foi possível interromper a execução agora.',
        )
        syncSessionOverHttp()
        return false
      } finally {
        abortRequestsRef.current.delete(sessionId)
        if (isCurrent()) setSubmissionPhase('idle')
      }
    })()
    abortRequestsRef.current.set(sessionId, request)
    return request
  }, [acceptSessionDetail, clearActivity, syncSessionOverHttp])

  const submitMessage = useCallback(async (
    content: string,
    files: File[],
    onError?: (pending: PendingSubmission) => void,
    onRecoveredCommit?: () => void,
    questionAnswer?: UserQuestionAnswer,
  ) => {
    const trimmed = content.trim()
    if (!trimmed) {
      if (files.length > 0) setAttachmentError('Escreva uma mensagem para enviar anexos.')
      return false
    }
    if (submissionPhase !== 'idle' || hydratingSessionRef.current) return false

    const validation = validateAgentChatFiles([], files)
    if (validation.error) {
      setAttachmentError(validation.error)
      return false
    }

    const existingRetry = retrySubmissionRef.current
    const matchingSteeringRetry = existingRetry?.kind === 'steering' &&
      pendingSubmissionMatchesInput(existingRetry, trimmed, validation.files)
    if (matchingSteeringRetry && session && !session.awaiting_response) {
      const accepted = session.messages.some((message) => (
        pendingSubmissionMatchesCommittedMessage(existingRetry, message)
      ))
      if (accepted) {
        retrySubmissionRef.current = null
        setSubmissionError(null)
        setAttachmentError(null)
        setSubmissionPhase('idle')
        existingRetry.onRecoveredCommit?.()
        return true
      }

      // A transport failure can leave delivery ambiguous even when the local
      // row says `failed`. Once the original run has ended, turning the same
      // text into a fresh turn could duplicate an instruction that OpenClaw
      // already received. Keep the draft until the user edits it explicitly.
      setSubmissionError(
        'Essa orientação não pôde ser confirmada antes do fim da execução. Edite a mensagem para enviá-la como um novo pedido.',
      )
      return false
    }

    if (questionAnswer && (!session || session.awaiting_response)) return false

    if (session?.awaiting_response) {
      // Keep the text command equivalent to the visible stop control. This is
      // especially useful in compact/legacy surfaces and never enters the
      // model's steering queue as ordinary text.
      if (validation.files.length === 0 && trimmed.toLowerCase() === '/stop') {
        const stopped = await abortSessionTurn(session.id)
        if (stopped) onRecoveredCommit?.()
        return stopped
      }

      const retry = retrySubmissionRef.current
      const recoveredSteering = retry?.kind === 'steering' &&
        pendingSubmissionMatchesInput(retry, trimmed, validation.files)
      const submissionId = recoveredSteering ? retry.submissionId : randomUuid()
      if (retry && !recoveredSteering) retrySubmissionRef.current = null
      const pending: PendingSubmission = recoveredSteering ? {
        ...retry,
        onError,
        onRecoveredCommit,
        pageContext: retry.pageContext,
      } : {
        kind: 'steering',
        submissionId,
        draft: trimmed,
        attachments: validation.files,
        onError,
        onRecoveredCommit,
        pageContext: getPageContext?.(),
      }
      const conversationGeneration = conversationGenerationRef.current
      const steeringSessionId = session.id
      const isCurrent = () => (
        conversationGenerationRef.current === conversationGeneration &&
        activeSessionIdRef.current === steeringSessionId
      )
      const steerOptions = {
        attachmentSignedBlobIds: pending.attachmentSignedBlobIds,
        digitalWorkerId: digitalWorker?.id ?? null,
        clientSubmissionId: submissionId,
        pageContext: pending.pageContext,
        onUploadsComplete: () => {
          if (isCurrent()) setSubmissionPhase('sending')
        },
      }
      setSubmissionError(null)
      setAttachmentError(null)
      setSubmissionPhase(validation.files.length > 0 ? 'uploading' : 'sending')

      try {
        const updated = await steerSessionOverHttp(
          steeringSessionId,
          trimmed,
          validation.files,
          steerOptions,
        )
        if (!isCurrent()) return false

        setSubmissionPhase('idle')
        if (retrySubmissionRef.current?.submissionId === submissionId) {
          retrySubmissionRef.current = null
        }
        acceptSessionDetail(updated)
        onRecoveredCommit?.()
        return true
      } catch (error) {
        if (!isCurrent()) return false

        setSubmissionPhase('idle')
        pending.attachmentSignedBlobIds = steerOptions.attachmentSignedBlobIds
        retrySubmissionRef.current = pending
        setSubmissionError(
          error instanceof Error ? error.message : 'Não foi possível adicionar a instrução agora.',
        )
        pending.onError?.(pending)
        syncSessionOverHttp()
        return false
      }
    }

    const retry = retrySubmissionRef.current
    const submissionId = retry?.kind === 'message' &&
      pendingSubmissionMatchesInput(retry, trimmed, validation.files) &&
      JSON.stringify(retry.questionAnswer) === JSON.stringify(questionAnswer)
      ? retry.submissionId
      : randomUuid()
    if (retry && retry.submissionId !== submissionId) retrySubmissionRef.current = null

    return performSend(
      trimmed,
      validation.files,
      submissionId,
      retry?.submissionId === submissionId ? retry.attachmentSignedBlobIds : undefined,
      onError,
      onRecoveredCommit,
      retry?.submissionId === submissionId ? retry.pageContext : getPageContext?.(),
      questionAnswer,
    )
  }, [abortSessionTurn, acceptSessionDetail, getPageContext, performSend, session, submissionPhase, syncSessionOverHttp])

  const abortCurrentTurn = useCallback(async () => {
    const sessionId = session?.id
    if (!sessionId || !session.awaiting_response) return false

    return abortSessionTurn(sessionId)
  }, [
    abortSessionTurn,
    session?.awaiting_response,
    session?.id,
  ])

  const retryFailedTurn = useCallback(async () => {
    const sessionId = session?.id
    if (!sessionId || session.awaiting_response || !session.response_error) return false

    setSubmissionError(null)
    setSubmissionPhase('sending')
    try {
      const updated = await retrySessionOverHttp(sessionId)
      if (activeSessionIdRef.current !== sessionId) return false

      acceptSessionDetail(updated)
      restartActivity()
      setSubmissionPhase('idle')
      return true
    } catch (error) {
      if (activeSessionIdRef.current !== sessionId) return false

      setSubmissionPhase('idle')
      setSubmissionError(
        error instanceof Error ? error.message : 'Não foi possível tentar novamente agora.',
      )
      return false
    }
  }, [acceptSessionDetail, restartActivity, session])

  const submitMessageFeedback = useCallback(async (
    messageId: string,
    score: 'positive' | 'negative',
  ) => {
    const sessionId = session?.id
    if (!sessionId) return false

    try {
      const updated = await submitMessageFeedbackOverHttp(sessionId, messageId, score)
      if (activeSessionIdRef.current !== sessionId) return false

      acceptSessionDetail(updated)
      return true
    } catch (error) {
      if (activeSessionIdRef.current !== sessionId) return false

      setSubmissionError(
        error instanceof Error ? error.message : 'Não foi possível registrar a avaliação agora.',
      )
      return false
    }
  }, [acceptSessionDetail, session?.id])

  const branchConversation = useCallback(async (
    messageId: string,
    mode: 'branch' | 'edit' | 'regenerate',
    content?: string,
  ) => {
    const sessionId = session?.id
    if (!sessionId || session.awaiting_response) return false

    setSubmissionError(null)
    try {
      const branched = await branchSessionOverHttp({ sessionId, messageId, mode, content })
      if (activeSessionIdRef.current !== sessionId) return false

      resetConversation(branched)
      setSessions((current) => upsertSessionSummary(current, detailToSummary(branched)))
      onSessionChange?.(branched.id)
      return true
    } catch (error) {
      if (activeSessionIdRef.current !== sessionId) return false

      setSubmissionError(
        error instanceof Error ? error.message : 'Não foi possível criar a ramificação agora.',
      )
      return false
    }
  }, [onSessionChange, resetConversation, session])

  const renameSession = useCallback(async (sessionId: string, title: string) => {
    const trimmedTitle = title.trim()
    const currentTitle = sessions.find((item) => item.id === sessionId)?.title
    if (!trimmedTitle || trimmedTitle === currentTitle) return

    setRenameError(null)
    try {
      const renamedSession = await renameSessionOverHttp(sessionId, trimmedTitle)
      setSessions((current) => upsertSessionSummary(current, detailToSummary(renamedSession)))
      setSession((current) => {
        if (!current || current.id !== sessionId) return current

        return mergeSession(current, renamedSession)
      })
    } catch (error) {
      setRenameError(error instanceof Error ? error.message : 'Não foi possível renomear a conversa agora.')
    }
  }, [sessions])

  const pinSession = useCallback(async (sessionId: string, pinned: boolean) => {
    setRenameError(null)
    try {
      const updated = await pinSessionOverHttp(sessionId, pinned)
      setSessions((current) => upsertSessionSummary(current, detailToSummary(updated)))
      setSession((current) => current?.id === sessionId ? mergeSession(current, updated) : current)
    } catch (error) {
      setRenameError(error instanceof Error ? error.message : 'Não foi possível fixar a conversa agora.')
    }
  }, [])

  const moveSessionToProject = useCallback((sessionId: string, projectId: string | null) => {
    const targetProject = projectId ? projects.find((item) => item.id === projectId) : null
    if (projectId && !targetProject) return

    const previousSession = sessions.find((item) => item.id === sessionId)
    if (!previousSession || previousSession.fde_project_id || previousSession.project_id === projectId) return

    setRenameError(null)
    setSessions((current) => current.map((item) => (
      item.id === sessionId
        ? { ...item, project_id: projectId, project_name: targetProject?.name ?? null }
        : item
    )))
    setSession((current) => current?.id === sessionId
      ? { ...current, project_id: projectId, project_name: targetProject?.name ?? null }
      : current)

    router.patch(
      `${agentChatApiScope()}/agent_chat_sessions/${sessionId}/project`,
      { project_id: projectId },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
          setSessions((current) => current.map((item) => (
            item.id === sessionId ? { ...item, ...previousSession } : item
          )))
          setSession((current) => current?.id === sessionId
            ? { ...current, ...previousSession }
            : current)
          setRenameError('N\u00e3o foi poss\u00edvel mover a conversa para o projeto agora.')
        },
      },
    )
  }, [projects, sessions])

  const createProject = useCallback((name: string) => {
    const normalizedName = name.trim()
    if (!digitalWorker || !normalizedName || normalizedName.length > 80) return

    router.post(
      `${agentChatApiScope()}/agent_chat_projects`,
      { digital_worker_id: digitalWorker.id, name: normalizedName },
      {
        preserveScroll: true,
        preserveState: true,
      },
    )
  }, [digitalWorker])

  const renameProject = useCallback((projectId: string, name: string) => {
    const normalizedName = name.trim()
    const project = projects.find((item) => item.id === projectId)
    if (!project || !normalizedName || normalizedName.length > 80 || normalizedName === project.name) return

    router.patch(
      `${agentChatApiScope()}/agent_chat_projects/${projectId}`,
      { name: normalizedName },
      {
        preserveScroll: true,
        preserveState: true,
      },
    )
  }, [projects])

  const deleteProject = useCallback((projectId: string) => {
    if (!projects.some((item) => item.id === projectId)) return

    router.delete(`${agentChatApiScope()}/agent_chat_projects/${projectId}`, {
      preserveScroll: true,
      preserveState: true,
    })
  }, [projects])

  const archiveSession = useCallback(async (sessionId: string) => {
    setRenameError(null)
    try {
      await archiveSessionOverHttp(sessionId)
      removeSessionFromView(sessionId)
    } catch (error) {
      setRenameError(error instanceof Error ? error.message : 'Não foi possível arquivar a conversa agora.')
    }
  }, [removeSessionFromView])

  const deleteSession = useCallback(async (sessionId: string) => {
    setRenameError(null)
    try {
      await deleteSessionOverHttp(sessionId)
      removeSessionFromView(sessionId)
    } catch (error) {
      setRenameError(error instanceof Error ? error.message : 'Não foi possível excluir a conversa agora.')
    }
  }, [removeSessionFromView])

  const assistantIsRunning =
    (session?.awaiting_response ?? false) ||
    submissionPhase !== 'idle' ||
    hydratingSessionRef.current !== null
  const canStartNewConversation =
    submissionPhase === 'idle' &&
    httpSubmissionRef.current === null &&
    pendingSubmissionRef.current === null &&
    hydratingSessionRef.current === null
  const visibleError = renameError || submissionError || session?.response_error || null

  // Ativação imperativa de uma sessão específica: o widget flutuante usa
  // para restaurar a conversa persistida após um refresh e para adotar a
  // sessão de edição de Processos criada no servidor (kickoff oculto).
  const adoptSession = useCallback((nextSession: AgentChatSessionDetail) => {
    resetConversation(nextSession)
  }, [resetConversation])

  const openSessionById = useCallback(async (sessionId: string) => {
    const loaded = await loadSessionOverHttp(sessionId)
    resetConversation(loaded)
    return loaded
  }, [resetConversation])

  return {
    activity,
    session,
    sessions,
    projects,
    isNewConversation,
    attachmentError,
    submissionPhase,
    connectionStatus,
    assistantIsRunning,
    canStartNewConversation,
    visibleError,
    setAttachmentError,
    submitMessage,
    retryFailedTurn,
    submitMessageFeedback,
    branchConversation,
    abortCurrentTurn,
    handleNewConversation,
    renameSession,
    pinSession,
    moveSessionToProject,
    createProject,
    renameProject,
    deleteProject,
    archiveSession,
    deleteSession,
    adoptSession,
    openSessionById,
  }
}

function assistantReplyToMessageId(message: AgentChatMessage): string | null {
  if (message.role !== 'assistant') return null

  const replyToMessageId = message.metadata?.in_reply_to_message_id
  return typeof replyToMessageId === 'string' ? replyToMessageId : null
}

function agentEventMayCompleteTurn(event: AgentEvent): boolean {
  return [
    'TEXT_MESSAGE_END',
    'RUN_FINISHED',
    'RUN_ERROR',
    'RUN_CANCELLED',
  ].includes(event.event_type)
}

function assistantCompletesActiveTurn(
  session: AgentChatSessionDetail,
  message: AgentChatMessage,
): boolean {
  if (message.role !== 'assistant') return false
  if (!session.awaiting_response) {
    return !session.response_error || (
      session.failed_response_message_id !== null &&
      assistantReplyToMessageId(message) === session.failed_response_message_id
    )
  }

  const replyTurnId = message.metadata?.agent_chat_turn_id
  return typeof replyTurnId === 'string' && replyTurnId === session.pending_turn?.id
}

function applyRealtimeMessage(
  session: AgentChatSessionDetail,
  message: AgentChatMessage,
  committedSubmission: boolean,
): AgentChatSessionDetail {
  const nextMessages = upsertMessage(session.messages, message)
  const completesActiveTurn = assistantCompletesActiveTurn(session, message)
  return {
    ...session,
    awaiting_response:
      completesActiveTurn ? false : committedSubmission || session.awaiting_response,
    pending_turn: completesActiveTurn ? null : session.pending_turn,
    has_visible_user_message: session.has_visible_user_message || isVisibleUserMessage(message),
    last_message_at: message.created_at,
    message_count: nextMessages.length,
    messages: nextMessages,
    response_error: completesActiveTurn ? null : session.response_error,
    failed_response_message_id:
      completesActiveTurn ? null : session.failed_response_message_id,
  }
}

function upsertBufferedMessageEvent(
  events: BufferedMessageEvent[],
  nextEvent: BufferedMessageEvent,
): BufferedMessageEvent[] {
  const existingIndex = events.findIndex((event) => event.message.id === nextEvent.message.id)
  if (existingIndex === -1) return [...events, nextEvent]

  return events.map((event, index) => index === existingIndex ? {
    message: mergeNativeMessage(event.message, nextEvent.message),
    committedSubmission: event.committedSubmission || nextEvent.committedSubmission,
  } : event)
}

function sessionConfirmsSubmissionProtocol(
  session: AgentChatSessionDetail,
  submissionId: string,
): boolean {
  return session.submission_idempotency === true && session.messages.some(
    (message) => message.role === 'user' && message.client_submission_id === submissionId,
  )
}

function hasAssistantResponseFor(messages: AgentChatMessage[], userMessageId: string): boolean {
  return messages.some((message) => assistantReplyToMessageId(message) === userMessageId)
}

function pendingSubmissionMatchesMessage(pending: PendingSubmission, message: AgentChatMessage): boolean {
  return message.role === 'user' && message.client_submission_id === pending.submissionId
}

function pendingSubmissionMatchesCommittedMessage(
  pending: PendingSubmission,
  message: AgentChatMessage,
): boolean {
  if (!pendingSubmissionMatchesMessage(pending, message)) return false

  // Saving a meeting instruction is not proof that Fleet accepted delivery.
  if (message.metadata?.source === 'fde_live_meeting_operator') {
    return message.metadata.live_meeting_delivery === 'accepted'
  }

  if (pending.kind === 'steering') {
    return message.metadata?.steering === true &&
      message.metadata?.steering_status === 'accepted'
  }

  return message.metadata?.steering !== true
}

function pendingSubmissionMatchesInput(
  pending: PendingSubmission,
  content: string,
  attachments: File[],
): boolean {
  if (pending.draft !== content || pending.attachments.length !== attachments.length) return false

  return pending.attachments.every((file, index) => file === attachments[index])
}

function isTransportError(error: unknown): boolean {
  return error instanceof TypeError
}

async function withSingleTransportRetry<T>(
  request: () => Promise<T>,
  isCurrent: () => boolean,
  isCommitted: () => boolean,
): Promise<T> {
  try {
    return await request()
  } catch (error) {
    if (!isCurrent() || isCommitted() || !isTransportError(error)) throw error

    return request()
  }
}

function mergeSession(
  current: AgentChatSessionDetail,
  next: AgentChatSessionDetail,
): AgentChatSessionDetail {
  if (next.id !== current.id) return next

  const mergedMessages = mergeMessages(current.messages, next.messages)
  const nextMessageIds = new Set(next.messages.map((message) => message.id))
  const nextHasAllCurrentMessages = current.messages.every((message) => nextMessageIds.has(message.id))
  // Same-second detail snapshots can arrive out of order, so message completeness
  // must protect newer lifecycle fields when timestamps cannot distinguish them.
  const nextCanReplaceSessionState =
    nextHasAllCurrentMessages && !isOlderSessionSummary(next, current)
  const latestSessionState = nextCanReplaceSessionState ? next : current
  const pendingTurn = serializedPendingTurnEqual(
    current.pending_turn,
    latestSessionState.pending_turn,
  ) ? current.pending_turn : latestSessionState.pending_turn
  return {
    ...current,
    ...latestSessionState,
    pending_turn: pendingTurn,
    has_visible_user_message:
      current.has_visible_user_message ||
      next.has_visible_user_message ||
      mergedMessages.some(isVisibleUserMessage),
    messages: mergedMessages,
    message_count: Math.max(next.message_count, mergedMessages.length),
  }
}

function serializedPendingTurnEqual(
  left: AgentChatSessionDetail['pending_turn'],
  right: AgentChatSessionDetail['pending_turn'],
) {
  if (left === right) return true
  if (!left || !right) return false

  return left.id === right.id &&
    left.attempt === right.attempt &&
    left.status === right.status &&
    left.progress_message === right.progress_message &&
    left.created_at === right.created_at &&
    left.submitted_at === right.submitted_at &&
    left.expires_at === right.expires_at &&
    JSON.stringify(left.events ?? []) === JSON.stringify(right.events ?? [])
}

function detailToSummary(session: AgentChatSessionDetail): AgentChatSessionSummary {
  const { can_send: _canSend, messages: _messages, ...summary } = session
  return summary
}

function mergeBusinessProcessOutcomes(
  current: AgentChatSessionSummary,
  incoming: AgentChatSessionSummary,
): Pick<AgentChatSessionSummary, 'created_business_process' | 'revised_business_process' | 'procedure_presentation'> {
  const currentRevision = current.revised_business_process
  const incomingRevision = incoming.revised_business_process
  const useIncomingRevision = Boolean(
    incomingRevision && (
      !currentRevision ||
      !currentRevision.at ||
      (incomingRevision.at && incomingRevision.at >= currentRevision.at)
    ),
  )

  return {
    created_business_process:
      current.created_business_process ?? incoming.created_business_process,
    revised_business_process: useIncomingRevision ? incomingRevision : currentRevision,
    procedure_presentation:
      incoming.procedure_presentation ?? current.procedure_presentation,
  }
}

function upsertSessionSummary(
  sessions: AgentChatSessionSummary[],
  nextSession: AgentChatSessionSummary,
) {
  const existingSessionIndex = sessions.findIndex((session) => session.id === nextSession.id)
  if (existingSessionIndex === -1) {
    return sortSessionSummaries([...sessions, nextSession])
  }

  const existingSession = sessions[existingSessionIndex]
  if (isOlderSessionSummary(nextSession, existingSession)) return sessions

  return sortSessionSummaries(
    sessions.map((session) => (session.id === nextSession.id ? nextSession : session)),
  )
}

function isOlderSessionSummary(nextSession: AgentChatSessionSummary, currentSession: AgentChatSessionSummary) {
  const nextTime = timestampMicroseconds(nextSession.updated_at)
  const currentTime = timestampMicroseconds(currentSession.updated_at)
  return nextTime !== null && currentTime !== null && nextTime < currentTime
}

function timestampMicroseconds(timestamp: string): number | null {
  const milliseconds = Date.parse(timestamp)
  if (!Number.isFinite(milliseconds)) return null

  // Date.parse drops sub-millisecond precision even though PostgreSQL keeps it.
  const fractionalDigits = timestamp.match(/\.(\d+)/)?.[1]
  if (!fractionalDigits) return milliseconds * 1000

  const microsecondsWithinSecond = Number(fractionalDigits.slice(0, 6).padEnd(6, '0'))
  return Math.floor(milliseconds / 1000) * 1_000_000 + microsecondsWithinSecond
}

function isVisibleUserMessage(message: AgentChatMessage): boolean {
  return message.role === 'user' && message.metadata?.hidden_in_onboarding !== true
}

function sortSessionSummaries(sessions: AgentChatSessionSummary[]) {
  return [...sessions].sort(compareSessionSummaries)
}

function compareSessionSummaries(left: AgentChatSessionSummary, right: AgentChatSessionSummary) {
  if (left.pinned !== right.pinned) return left.pinned ? -1 : 1

  const leftTime = Date.parse(left.last_message_at || left.created_at)
  const rightTime = Date.parse(right.last_message_at || right.created_at)
  return rightTime - leftTime
}
